/**
 * Data layer for `bulk-lp-upload` (fs811).
 *
 * Wraps the backend staged-jobs flow at `/api/v1/bulk-lp-upload`:
 *   POST /                         -> dry-run preview (stores job w/ parsed rows + errors)
 *   GET  /job/:jobId               -> full job incl. committed rows
 *   GET  /campaign/:campaignId     -> paginated list of jobs for a campaign
 *
 * Raw `fetch()` is confined to this file per the dash contract.
 */

// ---------------------------------------------------------------------------
// Row shapes (mirrors backend serializers in apps/api/src/routes/bulk-lp-upload.ts)
// ---------------------------------------------------------------------------

export interface BulkLpRowError {
	row: number;
	code: string;
	message: string;
	field?: string;
}

export interface BulkLpJobRow {
	row_index: number;
	label: string;
	url: string;
	weight: number;
	is_default: boolean;
}

export interface BulkLpPreviewResponse {
	jobId: string;
	parsed: number;
	created: number;
	error_count: number;
	errors: BulkLpRowError[];
	duplicate_labels: string[];
}

export interface BulkLpJobDetail {
	job_id: string;
	tenant_id: string;
	campaign_id: string;
	parsed: number;
	created: number;
	error_count: number;
	errors: BulkLpRowError[];
	duplicate_labels: string[];
	created_at: number;
	rows: BulkLpJobRow[];
}

export interface BulkLpJobSummary {
	job_id: string;
	tenant_id: string;
	campaign_id: string;
	parsed: number;
	created: number;
	error_count: number;
	errors: BulkLpRowError[];
	duplicate_labels: string[];
	created_at: number;
}

export interface BulkLpCampaignListResponse {
	campaign_id: string;
	total: number;
	limit: number;
	offset: number;
	jobs: BulkLpJobSummary[];
}

// ---------------------------------------------------------------------------
// Validators (pure, exported for unit tests)
// ---------------------------------------------------------------------------

export function isValidCampaignId(v: unknown): v is string {
	return typeof v === "string" && v.trim().length > 0 && v.length <= 128;
}

export function hasRequiredCsvHeader(csvText: string): boolean {
	const first = csvText.split(/\r?\n/)[0] ?? "";
	const cols = first.split(",").map((c) => c.trim().toLowerCase());
	return cols.includes("label") && cols.includes("url");
}

export function countCsvRows(csvText: string): number {
	const lines = csvText.split(/\r?\n/);
	let n = 0;
	for (let i = 1; i < lines.length; i++) {
		if (lines[i]?.trim().length) n += 1;
	}
	return n;
}

export function formatErrorCount(errors: BulkLpRowError[]): string {
	const n = errors.length;
	return n === 1 ? "1 error" : `${n} errors`;
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

const BASE = "/api/v1/bulk-lp-upload";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

// ---------------------------------------------------------------------------
// Network surface — only callable via hooks
// ---------------------------------------------------------------------------

export interface PreviewBody {
	campaignId: string;
	csvText: string;
}

export async function previewUpload(body: PreviewBody): Promise<BulkLpPreviewResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify({ campaignId: body.campaignId, csvText: body.csvText }),
	});
	return parseOrThrow<BulkLpPreviewResponse>(res);
}

export async function getJob(jobId: string): Promise<BulkLpJobDetail> {
	const res = await fetch(`${BASE}/job/${encodeURIComponent(jobId)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<BulkLpJobDetail>(res);
}

export async function listJobsForCampaign(
	campaignId: string,
	opts: { limit?: number; offset?: number } = {},
): Promise<BulkLpCampaignListResponse> {
	const params = new URLSearchParams();
	if (opts.limit != null) params.set("limit", String(opts.limit));
	if (opts.offset != null) params.set("offset", String(opts.offset));
	const qs = params.toString();
	const url = `${BASE}/campaign/${encodeURIComponent(campaignId)}${qs ? `?${qs}` : ""}`;
	const res = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<BulkLpCampaignListResponse>(res);
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const bulkLpUploadQueryKeys = {
	all: ["bulk-lp-upload"] as const,
	job: (jobId: string) => [...["bulk-lp-upload"] as const, "job", jobId] as const,
	campaign: (campaignId: string) =>
		[...["bulk-lp-upload"] as const, "campaign", campaignId] as const,
};
